/**
 * [화면 편집] 에 넣는 기본 요소 · 그림 영역 · 입력 칸의 값(2026-10-30).
 *
 * - **넣기**: 제목·글·목록·단추·표·구분선·상자·그림 영역·입력 칸(글·숫자·날짜)·선택 상자·체크 상자. 모두 인라인 스타일만
 *   가진 평범한 HTML 이다 — 만든 사람이 다시 열어도 알아보고, 안드로이드 5.1 웹뷰(Chromium 39)에서도 같게 그려진다.
 * - **그림 영역**: 자리와 크기를 먼저 잡아 두고 그림은 나중에 고르는 틀(`data-so-image`). 그림을 고르면 틀 안에 꽉 차게
 *   (`cover`) 또는 다 보이게(`contain`) 들어간다. 틀은 그림이 바뀌어도 자리·크기를 지킨다.
 * - **입력 칸의 값**: 편집기에서 정한 값은 `value`·`selected`·`checked` 로 적고 `data-so-value` 로도 표시한다. 문서의 스크립트는
 *   흔히 저장해 둔 값(localStorage)을 먼저 되살리거나, 처음 값으로는 계산하지 않고 바뀔 때(change)만 계산한다(무재해 현황판이
 *   그렇다). 그래서 저장할 때 문서 끝에 작은 스크립트(`VALUES_SCRIPT`)를 붙여, 화면에서 문서가 뜨면 정한 값을 다시 넣고
 *   input·change 를 알린다 — 문서가 원래 듣던 대로 계산이 돈다. 편집 화면은 스크립트를 돌리지 않아 계산된 숫자는 비어 보인다.
 * - **D-day**: 기준일(직접 고르거나 화면의 날짜 입력 칸에 연결)까지 남은 날(D-12·D-DAY·D+3) 또는 지난 날(n일째)을 그리는
 *   글(`data-so-dday`). 편집 화면은 스크립트를 돌리지 않으므로 **편집기가 직접** 계산해 글을 바꾸고(`refreshDday`), 화면에서는
 *   문서 끝의 스크립트(`DDAY_SCRIPT`)가 뜰 때·날짜 칸이 바뀔 때·1분마다(자정을 넘기면) 같은 셈(`ddayText`)으로 다시 그린다.
 * - **영상**: 그림처럼 문서에 품기엔 커서 콘텐츠로 따로 올리고 문서에는 그 자산 id 만 적는다(`<video data-so-video>`,
 *   `@signageone/core` 의 `html-media.ts`). 문서 끝의 스크립트(`VIDEO_SCRIPT`)가 영상의 자리를 알리면 화면이 칸 위에 겹쳐
 *   받아 둔 파일로 튼다. 편집 화면에는 콘솔의 미리보기 주소를 잠깐 달아 보이고, 저장할 때 뗀다(`dropVideoSources`).
 */
import { HTML_MEDIA_REQUEST, VIDEO_ATTR } from '@signageone/core';
import { DDAY_ATTR, IMAGE_FRAME_ATTR, setInline } from './dom.js';

export const VALUE_ATTR = 'data-so-value';
const SCRIPT_ATTR = 'data-so-values';
const DDAY_SCRIPT_ATTR = 'data-so-dday-script';
const VIDEO_SCRIPT_ATTR = 'data-so-video-script';

export type ImageFit = 'cover' | 'contain';

export type ComponentId =
  | 'heading'
  | 'text'
  | 'dday'
  | 'list'
  | 'button'
  | 'table'
  | 'divider'
  | 'box'
  | 'imageFrame'
  | 'image'
  | 'video'
  | 'input'
  | 'number'
  | 'date'
  | 'select'
  | 'checkbox';

export interface ComponentInfo {
  id: ComponentId;
  label: string;
  group: '글' | '그림·모양' | '입력';
  hint: string;
}

/** 넣기 메뉴의 차례. `image`·`video` 는 파일을 고른 뒤에 넣으므로 편집기가 따로 다룬다. */
export const COMPONENTS: readonly ComponentInfo[] = [
  { id: 'heading', label: '제목', group: '글', hint: '크고 굵은 글' },
  { id: 'text', label: '글', group: '글', hint: '보통 크기의 글' },
  { id: 'list', label: '목록', group: '글', hint: '• 로 시작하는 줄' },
  { id: 'button', label: '단추', group: '글', hint: '색 바탕의 글' },
  { id: 'dday', label: 'D-day', group: '글', hint: '기준일까지 남은 날·지난 날' },
  { id: 'imageFrame', label: '그림 영역', group: '그림·모양', hint: '자리를 잡고 그림은 나중에' },
  { id: 'image', label: '그림', group: '그림·모양', hint: '내 컴퓨터에서 고르기' },
  { id: 'video', label: '영상', group: '그림·모양', hint: '내 컴퓨터에서 고르기 — 콘텐츠로도 올라갑니다' },
  { id: 'box', label: '상자', group: '그림·모양', hint: '색 바탕의 네모' },
  { id: 'divider', label: '구분선', group: '그림·모양', hint: '가로 줄' },
  { id: 'table', label: '표', group: '그림·모양', hint: '3줄 × 3칸' },
  { id: 'input', label: '입력 칸', group: '입력', hint: '글을 넣는 칸' },
  { id: 'number', label: '숫자 입력', group: '입력', hint: '숫자만 넣는 칸' },
  { id: 'date', label: '날짜 입력', group: '입력', hint: '달력으로 고르는 칸' },
  { id: 'select', label: '선택 상자', group: '입력', hint: '항목 가운데 하나' },
  { id: 'checkbox', label: '체크 상자', group: '입력', hint: '켜고 끄는 네모' },
];

export interface BuildContext {
  /** 편집 크기 — 글자 크기·요소 크기를 화면에 맞춰 정한다. */
  width: number;
  height: number;
  /** 바탕 위에서 읽히는 글자 색(어두운 바탕이면 흰색). */
  ink: string;
  accent: string;
}

/** 넣을 요소와 처음 크기. `grow` 면 글처럼 내용만큼 늘어나게 크기를 굳히지 않는다. */
export interface Built {
  element: HTMLElement;
  width: number;
  height: number;
  grow: boolean;
}

const css = (declarations: Record<string, string | number>): string =>
  Object.entries(declarations)
    .map(([name, value]) => `${name}: ${value};`)
    .join(' ');

/** 입력 칸의 공통 모양 — 화면에서 멀리서도 보이는 크기. */
function fieldStyle(context: BuildContext, size: number): string {
  return css({
    'font-size': `${size}px`,
    padding: `${Math.round(size * 0.35)}px ${Math.round(size * 0.5)}px`,
    border: '2px solid #94a3b8',
    'border-radius': `${Math.round(size * 0.3)}px`,
    background: '#ffffff',
    color: '#111827',
    'box-sizing': 'border-box',
    'font-family': 'inherit',
  });
}

export function buildComponent(
  id: Exclude<ComponentId, 'image' | 'video'>,
  document_: Document,
  context: BuildContext,
): Built {
  const { width, height, ink, accent } = context;
  const unit = Math.min(width, height);
  const element = (tag: string, style: string, text?: string): HTMLElement => {
    const made = document_.createElement(tag);
    made.setAttribute('style', style);
    if (text !== undefined) made.textContent = text;
    return made;
  };
  const textSize = Math.max(14, Math.round(unit / 30));
  switch (id) {
    case 'heading': {
      const size = Math.max(24, Math.round(unit / 12));
      return {
        element: element(
          'h2',
          css({
            margin: 0,
            'font-size': `${size}px`,
            'font-weight': 700,
            'line-height': 1.25,
            color: ink,
            'white-space': 'pre-wrap',
          }),
          '제목을 쓰세요',
        ),
        width: size * 7,
        height: size * 1.25,
        grow: true,
      };
    }
    case 'text':
      return {
        element: element(
          'p',
          css({ margin: 0, 'font-size': `${textSize}px`, 'line-height': 1.5, color: ink, 'white-space': 'pre-wrap' }),
          '여기에 글을 쓰세요',
        ),
        width: textSize * 10,
        height: textSize * 1.5,
        grow: true,
      };
    case 'list': {
      const list = element(
        'ul',
        css({
          margin: 0,
          'padding-left': `${Math.round(textSize * 1.4)}px`,
          'font-size': `${textSize}px`,
          'line-height': 1.6,
          color: ink,
        }),
      );
      for (const text of ['첫째 줄', '둘째 줄', '셋째 줄']) list.append(element('li', '', text));
      return { element: list, width: textSize * 10, height: textSize * 1.6 * 3, grow: true };
    }
    case 'button': {
      const size = Math.max(16, Math.round(unit / 26));
      return {
        element: element(
          'div',
          css({
            display: 'inline-block',
            padding: `${Math.round(size * 0.55)}px ${Math.round(size * 1.2)}px`,
            'border-radius': `${Math.round(size * 0.5)}px`,
            background: accent,
            color: '#ffffff',
            'font-size': `${size}px`,
            'font-weight': 700,
            'text-align': 'center',
            'white-space': 'nowrap',
          }),
          '단추',
        ),
        width: size * 5,
        height: size * 2.1,
        grow: true,
      };
    }
    case 'table': {
      const size = Math.max(14, Math.round(unit / 36));
      const cell = (header: boolean): string =>
        css({
          border: '1px solid #cbd5e1',
          padding: `${Math.round(size * 0.5)}px ${Math.round(size * 0.7)}px`,
          'text-align': 'left',
          ...(header ? { background: '#f1f5f9', 'font-weight': 700 } : {}),
        });
      const table = element(
        'table',
        css({
          'border-collapse': 'collapse',
          width: '100%',
          'font-size': `${size}px`,
          color: '#111827',
          background: '#ffffff',
        }),
      );
      const rows = [
        ['항목', '내용', '비고'],
        ['', '', ''],
        ['', '', ''],
      ];
      rows.forEach((row, index) => {
        const line = document_.createElement('tr');
        for (const text of row) line.append(element(index === 0 ? 'th' : 'td', cell(index === 0), text || ' '));
        table.append(line);
      });
      const tableWidth = Math.round(width * 0.4);
      return { element: table, width: tableWidth, height: size * 2.6 * 3, grow: false };
    }
    case 'divider':
      return {
        element: element('div', css({ background: ink, opacity: 0.4 })),
        width: Math.round(width * 0.4),
        height: Math.max(2, Math.round(unit / 270)),
        grow: false,
      };
    case 'box':
      return {
        element: element('div', css({ 'background-color': '#e5e7eb', 'border-radius': '16px' })),
        width: Math.round(width * 0.25),
        height: Math.round(height * 0.2),
        grow: false,
      };
    case 'imageFrame': {
      const frame = element('div', emptyFrameStyle(Math.max(14, Math.round(unit / 40))), '그림을 넣을 자리');
      frame.setAttribute(IMAGE_FRAME_ATTR, 'cover');
      return { element: frame, width: Math.round(width * 0.3), height: Math.round(height * 0.35), grow: false };
    }
    case 'input':
    case 'number':
    case 'date': {
      const size = Math.max(16, Math.round(unit / 34));
      const input = element('input', fieldStyle(context, size)) as HTMLInputElement;
      input.setAttribute('type', id === 'input' ? 'text' : id);
      if (id === 'input') input.setAttribute('placeholder', '여기에 입력');
      return { element: input, width: id === 'date' ? size * 9 : size * 12, height: size * 2.3, grow: false };
    }
    case 'select': {
      const size = Math.max(16, Math.round(unit / 34));
      const select = element('select', fieldStyle(context, size));
      for (const text of ['항목 1', '항목 2', '항목 3']) {
        const option = document_.createElement('option');
        option.value = text;
        option.textContent = text;
        select.append(option);
      }
      return { element: select, width: size * 9, height: size * 2.3, grow: false };
    }
    case 'dday': {
      const size = Math.max(32, Math.round(unit / 9));
      const dday = element(
        'div',
        css({ 'font-size': `${size}px`, 'font-weight': 800, 'line-height': 1.1, color: ink, 'white-space': 'nowrap' }),
      );
      const target = new Date();
      target.setDate(target.getDate() + 30);
      writeDday(dday, { date: isoDate(target), from: null, mode: 'until', before: '', after: '' });
      return { element: dday, width: size * 3, height: size * 1.1, grow: true };
    }
    case 'checkbox': {
      const size = Math.max(16, Math.round(unit / 34));
      const label = element(
        'label',
        css({ display: 'inline-block', 'font-size': `${size}px`, color: ink, 'white-space': 'nowrap' }),
      );
      const box = element(
        'input',
        css({
          width: `${size}px`,
          height: `${size}px`,
          margin: `0 ${Math.round(size * 0.4)}px 0 0`,
          'vertical-align': 'middle',
        }),
      );
      box.setAttribute('type', 'checkbox');
      label.append(box, document_.createTextNode('확인했습니다'));
      return { element: label, width: size * 8, height: size * 1.4, grow: true };
    }
  }
}

/** 비어 있는 그림 영역 — 점선 테두리와 안내 글. 화면에도 이대로 나가니 그림을 고르라는 뜻이 보여야 한다. */
function emptyFrameStyle(size: number): string {
  return css({
    overflow: 'hidden',
    display: 'flex',
    'align-items': 'center',
    'justify-content': 'center',
    'text-align': 'center',
    'box-sizing': 'border-box',
    'border-radius': '12px',
    border: '3px dashed #94a3b8',
    'background-color': '#f1f5f9',
    color: '#64748b',
    'font-size': `${size}px`,
    'font-weight': 600,
  });
}

export function frameImage(frame: Element): HTMLImageElement | null {
  return frame.querySelector('img');
}

export function frameFit(frame: Element): ImageFit {
  return frame.getAttribute(IMAGE_FRAME_ATTR) === 'contain' ? 'contain' : 'cover';
}

const frameImageStyle = (fit: ImageFit): string =>
  css({ display: 'block', width: '100%', height: '100%', 'object-fit': fit, margin: 0, border: 0 });

/**
 * 그림 영역에 그림을 넣는다 — 안내 글과 점선을 걷고 그림 하나만 남긴다. 틀의 자리·크기·둥글기는 그대로, 바탕 색은 남겨
 * 다 보이게(`contain`) 넣었을 때 빈 곳을 채운다.
 */
export function fillImageFrame(frame: HTMLElement, source: string): void {
  const fit = frameFit(frame);
  frame.textContent = '';
  const image = frame.ownerDocument.createElement('img');
  image.setAttribute('alt', '');
  image.setAttribute('src', source);
  image.setAttribute('style', frameImageStyle(fit));
  frame.append(image);
  setInline(frame, { border: null });
}

export function setFrameFit(frame: HTMLElement, fit: ImageFit): void {
  frame.setAttribute(IMAGE_FRAME_ATTR, fit);
  frameImage(frame)?.setAttribute('style', frameImageStyle(fit));
}

/** 그림을 빼고 빈 그림 영역으로 — 안내 글을 되살린다. */
export function clearImageFrame(frame: HTMLElement): void {
  frame.textContent = '그림을 넣을 자리';
  setInline(frame, { border: '3px dashed #94a3b8' });
}

// ── 입력 칸의 값

export type FieldKind = 'text' | 'number' | 'date' | 'time' | 'check' | 'select' | 'textarea' | 'color' | 'range';

/** 값을 정할 수 있는 입력 칸인가 — 그 종류. 단추형(submit·button·file 등)은 아니다. */
export function fieldKind(element: Element): FieldKind | null {
  const tag = element.tagName.toLowerCase();
  if (tag === 'select') return 'select';
  if (tag === 'textarea') return 'textarea';
  if (tag !== 'input') return null;
  const type = (element.getAttribute('type') ?? 'text').toLowerCase();
  if (type === 'checkbox' || type === 'radio') return 'check';
  if (type === 'number') return 'number';
  if (type === 'range') return 'range';
  if (type === 'date' || type === 'datetime-local' || type === 'month' || type === 'week') return 'date';
  if (type === 'time') return 'time';
  if (type === 'color') return 'color';
  if (['text', 'search', 'email', 'tel', 'url', 'password', ''].includes(type)) return 'text';
  return null;
}

/** 입력 칸의 이름 — 붙인 이름 · aria-label · 짝지은 label 글 · 감싼 label 글 · 안내 글 차례로. 없으면 빈 글. */
export function fieldLabel(element: Element): string {
  const named = element.getAttribute('data-name') ?? element.getAttribute('aria-label');
  if (named?.trim()) return named.trim();
  const id = element.getAttribute('id');
  const paired = id ? element.ownerDocument.querySelector(`label[for="${id.replace(/"/g, '\\"')}"]`) : null;
  const text = (paired ?? element.closest('label'))?.textContent?.replace(/\s+/g, ' ').trim();
  if (text) return text;
  return element.getAttribute('placeholder')?.trim() ?? '';
}

/** 지금 값 — 화면에 보이는 값(속성이 아니라 요소의 값). 체크면 'true'·'false'. */
export function fieldValue(element: Element): string {
  const field = element as HTMLInputElement & HTMLSelectElement & HTMLTextAreaElement;
  if (fieldKind(element) === 'check') return String(field.checked);
  return field.value ?? '';
}

/**
 * 값을 정한다 — 문서에 남도록 속성으로 적고(`value`·`selected`·`checked`·글), 화면에서 다시 넣을 값을 `data-so-value` 에.
 * 선택 상자는 그 값의 항목에만 `selected` 를 단다.
 */
export function setFieldValue(element: HTMLElement, value: string): void {
  const kind = fieldKind(element);
  if (!kind) return;
  if (kind === 'check') {
    const checked = value === 'true';
    if (checked) element.setAttribute('checked', '');
    else element.removeAttribute('checked');
    (element as HTMLInputElement).checked = checked;
  } else if (kind === 'select') {
    for (const option of Array.from((element as HTMLSelectElement).options)) {
      if (option.value === value) option.setAttribute('selected', '');
      else option.removeAttribute('selected');
    }
    (element as HTMLSelectElement).value = value;
  } else if (kind === 'textarea') {
    element.textContent = value;
    (element as HTMLTextAreaElement).value = value;
  } else {
    element.setAttribute('value', value);
    (element as HTMLInputElement).value = value;
  }
  element.setAttribute(VALUE_ATTR, value);
}

/** 선택 상자의 항목을 한 줄에 하나씩 받아 다시 만든다 — 값은 글과 같게. 지금 값이 남아 있으면 그대로 고른다. */
export function setSelectOptions(select: HTMLSelectElement, lines: readonly string[]): void {
  const current = select.value;
  const document_ = select.ownerDocument;
  select.textContent = '';
  for (const line of lines) {
    const option = document_.createElement('option');
    option.value = line;
    option.textContent = line;
    if (line === current) option.setAttribute('selected', '');
    select.append(option);
  }
  if (select.getAttribute(VALUE_ATTR) !== null && !lines.includes(select.getAttribute(VALUE_ATTR)!))
    select.removeAttribute(VALUE_ATTR);
}

/**
 * 화면에서 정한 값을 다시 넣는 스크립트 — ES5 로(안드로이드 5.1 웹뷰). 문서 자신의 스크립트 뒤(본문 끝)에서 한 번,
 * 다 읽힌 뒤(load) 한 번 더 — 문서가 DOMContentLoaded 에 리스너를 다는 경우도 받게. 같은 계산을 두 번 해도 결과는 같다.
 */
export const VALUES_SCRIPT = [
  '(function () {',
  "  function fire(el, type) { var e = document.createEvent('HTMLEvents'); e.initEvent(type, true, false); el.dispatchEvent(e); }",
  '  function apply() {',
  `    var list = document.querySelectorAll('[${VALUE_ATTR}]');`,
  '    for (var i = 0; i < list.length; i++) {',
  `      var el = list[i]; var v = el.getAttribute('${VALUE_ATTR}');`,
  "      if (el.type === 'checkbox' || el.type === 'radio') el.checked = v === 'true'; else el.value = v;",
  "      fire(el, 'input'); fire(el, 'change');",
  '    }',
  '  }',
  '  apply();',
  "  window.addEventListener('load', apply);",
  '})();',
].join('\n');

/** 저장할 문서(복제본)에 값 스크립트를 맞춘다 — 정한 값이 있으면 본문 끝에 하나, 없으면 뺀다. */
export function syncRuntimeScripts(root: Element): void {
  for (const old of Array.from(
    root.querySelectorAll(`script[${SCRIPT_ATTR}], script[${DDAY_SCRIPT_ATTR}], script[${VIDEO_SCRIPT_ATTR}]`),
  ))
    old.remove();
  const body = root.querySelector('body') ?? root;
  const add = (attribute: string, text: string): void => {
    const script = root.ownerDocument.createElement('script');
    script.setAttribute(attribute, '');
    script.textContent = text;
    body.append(script);
  };
  // 값을 먼저 넣고 D-day 가 그 값을 읽는다.
  if (root.querySelector(`[${VALUE_ATTR}]`)) add(SCRIPT_ATTR, VALUES_SCRIPT);
  if (root.querySelector(`[${DDAY_ATTR}]`)) add(DDAY_SCRIPT_ATTR, DDAY_SCRIPT);
  if (root.querySelector(`video[${VIDEO_ATTR}]`)) add(VIDEO_SCRIPT_ATTR, VIDEO_SCRIPT);
}

// ── 영상

/** 새 영상 — 소리 없이, 되풀이해, 저절로 튼다(화면은 누를 사람이 없다). 주소는 달지 않는다(`html-media.ts`). */
export function createVideo(document_: Document, id: string): HTMLVideoElement {
  const video = document_.createElement('video');
  video.setAttribute(VIDEO_ATTR, id);
  for (const name of ['muted', 'loop', 'autoplay', 'playsinline']) video.setAttribute(name, '');
  video.setAttribute('style', css({ display: 'block', 'object-fit': 'cover', 'background-color': '#000000', margin: 0, border: 0 }));
  return video;
}

export function videoFit(video: Element): ImageFit {
  return (video as HTMLElement).style.objectFit === 'contain' ? 'contain' : 'cover';
}

export function setVideoFit(video: HTMLElement, fit: ImageFit): void {
  setInline(video, { 'object-fit': fit });
}

/** 소리를 내나 — `muted` 가 없으면 낸다. */
export function videoSound(video: Element): boolean {
  return !video.hasAttribute('muted');
}

export function setVideoSound(video: HTMLVideoElement, on: boolean): void {
  if (on) video.removeAttribute('muted');
  else video.setAttribute('muted', '');
  video.muted = !on;
}

/** 다른 영상으로 — 자리·크기·맞춤은 그대로. */
export function setVideoAsset(video: HTMLVideoElement, id: string): void {
  video.setAttribute(VIDEO_ATTR, id);
  video.removeAttribute('src');
}

/** 저장할 문서(복제본)에서 편집 화면에만 단 미리보기 주소를 뗀다 — 콘솔의 서명 주소가 문서에 남지 않게. */
export function dropVideoSources(root: Element): void {
  for (const video of Array.from(root.querySelectorAll(`video[${VIDEO_ATTR}]`))) {
    video.removeAttribute('src');
    video.removeAttribute('poster');
  }
}

/**
 * 영상의 자리를 알리는 스크립트 — ES5 로(안드로이드 5.1 웹뷰). 본문 끝에서 한 번, 다 읽힌 뒤·창 크기가 바뀔 때 다시 부모(화면·
 * 콘솔 미리보기)에게 영상마다 자리(이 창 안의 좌표·크기)·맞춤·소리를 알린다(`HTML_MEDIA_REQUEST`). 부모가 칸 위에 겹친 층에
 * 그 자리대로 영상을 튼다 — 5.1 웹뷰는 출처 없는 칸 안의 Blob 영상을 틀지 못한다(`@signageone/core` 의 `html-media.ts`).
 * 부모가 없거나 모르면(옛 화면) 그 자리는 검은 채로 남는다.
 */
export const VIDEO_SCRIPT = [
  '(function () {',
  '  if (!window.parent || window.parent === window) return;',
  '  function send() {',
  `    var list = document.querySelectorAll('video[${VIDEO_ATTR}]'), videos = [];`,
  '    for (var i = 0; i < list.length; i++) {',
  '      var r = list[i].getBoundingClientRect();',
  '      if (r.width < 1 || r.height < 1) continue;',
  "      var fit = (list[i].style.objectFit || window.getComputedStyle(list[i]).objectFit) === 'contain' ? 'contain' : 'cover';",
  `      videos.push({ id: list[i].getAttribute('${VIDEO_ATTR}'), x: r.left, y: r.top, width: r.width, height: r.height, fit: fit, muted: list[i].hasAttribute('muted') });`,
  '    }',
  `    if (videos.length) window.parent.postMessage({ type: '${HTML_MEDIA_REQUEST}', videos: videos }, '*');`,
  '  }',
  '  send();',
  "  window.addEventListener('load', send);",
  "  window.addEventListener('resize', send);",
  '})();',
].join('\n');

// ── D-day

export type DdayMode = 'until' | 'since';

export interface DdaySettings {
  /** 직접 고른 기준일(YYYY-MM-DD) — `from` 이 있으면 그 칸의 값이 앞선다. */
  date: string;
  /** 연결한 날짜 입력 칸의 id. */
  from: string | null;
  /** 남은 날(D-12 · D-DAY · D+3) · 지난 날(기준일이 1일째). */
  mode: DdayMode;
  before: string;
  after: string;
}

const DDAY_FROM = 'data-so-dday-from';
const DDAY_MODE = 'data-so-dday-mode';
const DDAY_BEFORE = 'data-so-dday-before';
const DDAY_AFTER = 'data-so-dday-after';

export const isoDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const dayNumber = (year: number, month: number, day: number): number =>
  Math.round(Date.UTC(year, month - 1, day) / 86400000);

const withCommas = (value: number): string => String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/**
 * D-day 글 — 기준일 `value`(YYYY-MM-DD)와 오늘(`today`, 그 기기의 날짜)로. 기준일이 없거나 읽을 수 없으면 '-'.
 * 화면의 `DDAY_SCRIPT` 와 같은 셈이다 — 둘을 함께 고친다(components.test.ts 가 맞춰 본다).
 */
export function ddayText(
  settings: Pick<DdaySettings, 'mode' | 'before' | 'after'>,
  value: string,
  today: Date,
): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  let core = '-';
  if (match) {
    const diff =
      dayNumber(Number(match[1]), Number(match[2]), Number(match[3])) -
      dayNumber(today.getFullYear(), today.getMonth() + 1, today.getDate());
    if (settings.mode === 'since') core = withCommas(Math.max(0, 1 - diff));
    else core = diff > 0 ? `D-${withCommas(diff)}` : diff === 0 ? 'D-DAY' : `D+${withCommas(-diff)}`;
  }
  return `${settings.before}${core}${settings.after}`;
}

export function readDday(element: Element): DdaySettings {
  return {
    date: element.getAttribute(DDAY_ATTR) ?? '',
    from: element.getAttribute(DDAY_FROM),
    mode: element.getAttribute(DDAY_MODE) === 'since' ? 'since' : 'until',
    before: element.getAttribute(DDAY_BEFORE) ?? '',
    after: element.getAttribute(DDAY_AFTER) ?? '',
  };
}

/** D-day 설정을 적고 글을 바로 다시 그린다. */
export function writeDday(element: HTMLElement, settings: DdaySettings): void {
  element.setAttribute(DDAY_ATTR, settings.date);
  const optional = (name: string, value: string | null): void => {
    if (value) element.setAttribute(name, value);
    else element.removeAttribute(name);
  };
  optional(DDAY_FROM, settings.from);
  optional(DDAY_MODE, settings.mode === 'since' ? 'since' : null);
  optional(DDAY_BEFORE, settings.before);
  optional(DDAY_AFTER, settings.after);
  refreshDday(element);
}

/** 기준일 — 연결한 날짜 칸이 있으면 그 칸의 지금 값, 없으면 직접 고른 날. */
export function ddayDate(element: Element): string {
  const settings = readDday(element);
  if (settings.from) {
    const source = element.ownerDocument.getElementById(settings.from) as HTMLInputElement | null;
    if (source) return source.value ?? '';
  }
  return settings.date;
}

export function refreshDday(element: HTMLElement, today = new Date()): void {
  const text = ddayText(readDday(element), ddayDate(element), today);
  if (element.textContent !== text) element.textContent = text;
}

/** 문서의 D-day 를 모두 다시 그린다 — 열 때(며칠 전에 저장한 글이면 오늘 기준으로)·날짜 칸의 값을 바꿨을 때. */
export function refreshDdays(root: ParentNode, today = new Date()): void {
  for (const element of Array.from(root.querySelectorAll<HTMLElement>(`[${DDAY_ATTR}]`))) refreshDday(element, today);
}

/** 연결할 수 있는 날짜 입력 칸 — id 가 없으면 하나 붙인다(그 칸을 가리키려면 이름이 있어야 한다). */
export function linkableDateFields(document_: Document): Array<{ id: string; element: HTMLInputElement }> {
  const out: Array<{ id: string; element: HTMLInputElement }> = [];
  for (const element of Array.from(document_.querySelectorAll<HTMLInputElement>('input'))) {
    if (fieldKind(element) !== 'date') continue;
    out.push({ id: element.id, element });
  }
  return out;
}

/** 날짜 칸에 id 가 없으면 겹치지 않는 것을 붙인다. */
export function ensureId(element: HTMLElement): string {
  if (element.id) return element.id;
  const document_ = element.ownerDocument;
  let index = 1;
  while (document_.getElementById(`so-date-${index}`)) index += 1;
  element.id = `so-date-${index}`;
  return element.id;
}

/**
 * 화면에서 D-day 를 다시 그리는 스크립트 — ES5(안드로이드 5.1 웹뷰). `ddayText` 와 같은 셈. 뜰 때·다 읽힌 뒤·날짜 칸이
 * 바뀔 때(input·change)·1분마다(자정을 넘기면 바뀌게) 그린다.
 */
export const DDAY_SCRIPT = [
  '(function () {',
  '  function day(y, m, d) { return Math.round(Date.UTC(y, m - 1, d) / 86400000); }',
  "  function commas(n) { return String(n).replace(/\\B(?=(\\d{3})+(?!\\d))/g, ','); }",
  '  function text(el) {',
  `    var from = el.getAttribute('${DDAY_FROM}'); var src = from ? document.getElementById(from) : null;`,
  `    var v = src ? src.value : el.getAttribute('${DDAY_ATTR}');`,
  "    var m = /^(\\d{4})-(\\d{2})-(\\d{2})/.exec(v || ''); var core = '-';",
  '    if (m) {',
  '      var now = new Date();',
  '      var diff = day(+m[1], +m[2], +m[3]) - day(now.getFullYear(), now.getMonth() + 1, now.getDate());',
  `      if (el.getAttribute('${DDAY_MODE}') === 'since') core = commas(Math.max(0, 1 - diff));`,
  "      else core = diff > 0 ? 'D-' + commas(diff) : diff === 0 ? 'D-DAY' : 'D+' + commas(-diff);",
  '    }',
  `    return (el.getAttribute('${DDAY_BEFORE}') || '') + core + (el.getAttribute('${DDAY_AFTER}') || '');`,
  '  }',
  '  function update() {',
  `    var list = document.querySelectorAll('[${DDAY_ATTR}]');`,
  '    for (var i = 0; i < list.length; i++) { var t = text(list[i]); if (list[i].textContent !== t) list[i].textContent = t; }',
  '  }',
  '  update();',
  "  window.addEventListener('load', update);",
  "  document.addEventListener('input', update, true);",
  "  document.addEventListener('change', update, true);",
  '  setInterval(update, 60000);',
  '})();',
].join('\n');

// ── 빈 화면

const escapeHtml = (text: string): string =>
  text.replace(/[&<>"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[char]!);

/**
 * 문서의 제목(`<title>`)을 바꾼다(2026-10-30) — 등록한 템플릿의 사본에 새 콘텐츠의 이름을 단다. 화면에는 나오지 않지만,
 * 고치지 않고 저장해도 사본이 원본과 다른 글자가 되어 새 콘텐츠로 남는다(같은 글자는 한 콘텐츠다). `<head>` 가 없으면 맨 앞에.
 */
export function withDocumentTitle(html: string, title: string): string {
  const tag = `<title>${escapeHtml(title)}</title>`;
  if (/<title\b[^>]*>[\s\S]*?<\/title>/i.test(html)) return html.replace(/<title\b[^>]*>[\s\S]*?<\/title>/i, () => tag);
  if (/<head\b[^>]*>/i.test(html)) return html.replace(/<head\b[^>]*>/i, (head) => `${head}${tag}`);
  return `${tag}${html}`;
}

/**
 * 빈 화면에서 새로 만들 때의 문서 — 흰 바탕 하나. 몸통이 화면 크기(100vw·100vh)를 채우고 자리의 기준이 된다
 * (`position: relative`). 넣은 요소는 몸통에 붙어 px 자리로 놓이고, 저장할 때 편집 크기를 콘텐츠의 기준 크기로 적어
 * 화면마다 통째로 맞춘다. 글꼴은 화면 기기에 있는 고딕을 차례로(안드로이드는 Noto Sans CJK, 윈도우는 맑은 고딕).
 */
export function blankDocument(title: string): string {
  return [
    '<!DOCTYPE html>',
    '<html lang="ko">',
    '<head>',
    '<meta charset="utf-8">',
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
    `<title>${escapeHtml(title)}</title>`,
    '<style>',
    'html, body { margin: 0; padding: 0; }',
    'body {',
    '  position: relative; width: 100vw; height: 100vh; overflow: hidden;',
    '  background: #ffffff; color: #111827;',
    '  font-family: "Noto Sans KR", "Noto Sans CJK KR", "Malgun Gothic", "Apple SD Gothic Neo", sans-serif;',
    '}',
    '</style>',
    '</head>',
    '<body></body>',
    '</html>',
    '',
  ].join('\n');
}
